import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, LineElement, PointElement,
  LinearScale, CategoryScale, Filler, Tooltip,
} from 'chart.js';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { sumAccountsBRL } from '../../utils/exchange';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip);

const MONTH_NAMES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

export default function Projection() {
  const { state } = useApp();

  const { labels, balances, diff } = useMemo(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totalBalance = sumAccountsBRL(state.accounts, state.usdRate);

    // Média histórica dos últimos 3 meses como fallback
    let sumIncome = 0, sumExpense = 0, countedMonths = 0;
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const txs = state.transactions.filter(t => t.date.startsWith(m) && t.status === 'paid' && !t.cardId);
      const inc = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
      const exp = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);
      if (inc > 0 || exp > 0) { sumIncome += inc; sumExpense += exp; countedMonths++; }
    }
    if (countedMonths === 0) {
      const txs = state.transactions.filter(t => t.date.startsWith(currentMonth) && t.status === 'paid' && !t.cardId);
      sumIncome  = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
      sumExpense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);
      countedMonths = 1;
    }
    const avgIncome  = sumIncome  / (countedMonths || 1);
    const avgExpense = sumExpense / (countedMonths || 1);

    const labels = [];
    const balances = [];
    let bal = totalBalance;

    for (let i = 0; i <= 11; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const mStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      labels.push(`${MONTH_NAMES[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`);
      balances.push(parseFloat(bal.toFixed(2)));

      // Mês atual: apenas pendentes (pagos já estão no saldo)
      // Meses futuros: todos os lançamentos cadastrados (pagos + pendentes)
      const isCurrentMonth = mStr === currentMonth;
      const monthTxs = state.transactions.filter(t =>
        t.date.startsWith(mStr) && !t.cardId &&
        (isCurrentMonth ? t.status === 'pending' : true)
      );
      const monthIncome  = monthTxs.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
      const monthExpense = monthTxs.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);

      if (monthIncome > 0 || monthExpense > 0) {
        bal += monthIncome - monthExpense;
      } else {
        bal += avgIncome - avgExpense;
      }
    }

    return { labels, balances, diff: balances[11] - balances[0] };
  }, [state]);

  const positive = diff >= 0;
  const trendColor = positive ? '#1d9e75' : '#e24b4a';

  const chartData = {
    labels,
    datasets: [{
      data: balances,
      borderColor: trendColor,
      backgroundColor: positive ? 'rgba(29,158,117,0.08)' : 'rgba(226,75,74,0.08)',
      borderWidth: 2.5,
      fill: true,
      tension: 0.35,
      pointBackgroundColor: (ctx) => ctx.dataIndex === 0 ? '#c9a227' : trendColor,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: (ctx) => (ctx.dataIndex === 0 || ctx.dataIndex === 11) ? 5 : 3,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => ` ${formatCurrency(ctx.raw)}` } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: 'rgba(128,128,128,0.7)', maxRotation: 0 },
      },
      y: {
        grid: { color: 'rgba(128,128,128,0.08)' },
        ticks: {
          font: { size: 10 }, color: 'rgba(128,128,128,0.7)',
          callback: v => Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0),
        },
      },
    },
  };

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: positive ? '#d1fae5' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-chart-line" style={{ color: trendColor, fontSize: 15 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Projeção 12 meses</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Baseado nos últimos 3 meses</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Variação projetada</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: trendColor }}>
            {diff >= 0 ? '+' : ''}{formatCurrency(diff)}
          </div>
        </div>
      </div>
      <div style={{ height: 160 }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}
